'use client';

import AppLayout from '@/components/AppLayout';
import { Users, Shield, Music2, Mail, Calendar } from 'lucide-react';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Collector';
  guitars: number;
  joined: string;
  lastActive: string;
  status: 'active' | 'inactive';
}

const mockUsers: User[] = [
  {
    id: 'user-001',
    name: 'Marcus Webb',
    email: 'marcus.webb@guitarvault.app',
    role: 'Admin',
    guitars: 12,
    joined: '01/01/2026',
    lastActive: '05/14/2026',
    status: 'active',
  },
  {
    id: 'user-002',
    name: 'Priya Sharma',
    email: 'priya.sharma@guitarvault.app',
    role: 'Collector',
    guitars: 7,
    joined: '02/15/2026',
    lastActive: '05/13/2026',
    status: 'active',
  },
  {
    id: 'user-003',
    name: 'Dmitri Volkov',
    email: 'dmitri.volkov@guitarvault.app',
    role: 'Collector',
    guitars: 4,
    joined: '03/10/2026',
    lastActive: '04/28/2026',
    status: 'active',
  },
  {
    id: 'user-004',
    name: 'Sofia Reyes',
    email: 'sofia.reyes@guitarvault.app',
    role: 'Collector',
    guitars: 2,
    joined: '04/01/2026',
    lastActive: '04/10/2026',
    status: 'inactive',
  },
];

export default function UsersPage() {
  const totalUsers = mockUsers.length;
  const admins = mockUsers.filter((u) => u.role === 'Admin').length;
  const activeUsers = mockUsers.filter((u) => u.status === 'active').length;

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage all GuitarVault users and their roles</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users size={18} className="text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{totalUsers}</p>
              <p className="text-xs text-muted-foreground font-medium">Total Users</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Shield size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{admins}</p>
              <p className="text-xs text-muted-foreground font-medium">Admins</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
              <Music2 size={18} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{activeUsers}</p>
              <p className="text-xs text-muted-foreground font-medium">Active</p>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Users size={16} className="text-primary" />
            <h2 className="text-sm font-semibold text-foreground">All Users</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Role</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Guitars</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Joined</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Active</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {mockUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-primary">
                            {user.name.split(' ').map((n) => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{user.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Mail size={10} />
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                        user.role === 'Admin' ?'bg-blue-50 text-blue-700 border border-blue-200' :'bg-muted text-muted-foreground border border-border'
                      }`}>
                        {user.role === 'Admin' && <Shield size={10} />}
                        {user.role}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Music2 size={13} className="text-primary" />
                        {user.guitars}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {user.joined}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">{user.lastActive}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${
                        user.status === 'active' ?'bg-green-50 text-green-700 border border-green-200' :'bg-muted text-muted-foreground border border-border'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${user.status === 'active' ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                        {user.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
